import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { resolveClinicForUser } from '../lib/clinicResolver';
import { useWarehouse } from '../contexts/WarehouseContext';
import RemoveItemsDialog from './RemoveItemsDialog';
import Sidebar from './Sidebar';
import LoadingSpinner from './LoadingSpinner';

const EditMedicalRecordPage = () => {
  const navigate = useNavigate();
  const { recordId } = useParams();
  const [user, setUser] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pet, setPet] = useState(null);
  
  // Warehouse integration
  const { items: warehouseItems, removeItems } = useWarehouse();
  const [showWarehouseDialog, setShowWarehouseDialog] = useState(false);
  const [pendingWarehouseChanges, setPendingWarehouseChanges] = useState([]);

  const [form, setForm] = useState({
    date: '',
    title: '',
    title_custom: '',
    weight: '',
    temperature: '',
    medication: '',
    notes: '',
    attachments: []
  });

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const toastTimeoutRef = useRef(null);

  // Cleanup toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  const showToast = (message, type = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, type });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 4000);
  };

  // Warehouse integration handlers
  const handleWarehouseRemove = async (itemsToRemove) => {
    try {
      // Store the changes for potential rollback (don't actually remove yet)
      setPendingWarehouseChanges(itemsToRemove);
      
      // Auto-fill medication field with removed items
      const medicationText = itemsToRemove.map(item => 
        `${item.name}: ${item.quantityToRemove} ${item.unit}`
      ).join('\n');
      
      setForm(prev => ({
        ...prev,
        medication: prev.medication ? `${prev.medication}\n\n${medicationText}` : medicationText
      }));
      
      showToast('Προϊόντα προστέθηκαν στα φάρμακα (θα αφαιρεθούν όταν αποθηκευτεί η εγγραφή)', 'success');
    } catch (error) {
      console.error('Error processing warehouse items:', error);
      showToast('Σφάλμα επεξεργασίας προϊόντων', 'error');
    }
  };

  const handleWarehouseDialogClose = () => {
    setShowWarehouseDialog(false);
  };

  const handleCancel = () => {
    // Clear pending warehouse changes (no rollback needed since we didn't actually remove anything)
    setPendingWarehouseChanges([]);
    navigate(-1);
  };

  useEffect(() => {
    checkUser();
  }, []);

  useEffect(() => {
    if (clinic && recordId) {
      fetchRecord();
    }
  }, [clinic, recordId]);

  const checkUser = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        navigate('/login');
        return;
      }
      setUser(user);
      const { clinic: clinicData } = await resolveClinicForUser(supabase, user.id);
      if (clinicData) setClinic(clinicData);
    } catch (err) {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecord = async () => {
    try {
      const { data, error } = await supabase
        .from('medical_records')
        .select('*')
        .eq('id', recordId)
        .single();
      if (error) throw error;

      // Load pet for back navigation
      const { data: petData } = await supabase
        .from('pets')
        .select('id, name, species')
        .eq('id', data.pet_id)
        .single();
      setPet(petData || null);

      // Map existing values into form
      const createdDate = data.created_at ? new Date(data.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      const isStandardTitle = ['check up', 'vaccination', 'surgery', 'emergency'].includes((data.title || '').toLowerCase());
      const attachmentsMapped = Array.isArray(data.attachments) 
        ? data.attachments.map(a => ({ title: (a && a.title) ? a.title : '', url: (a && a.url) ? a.url : (typeof a === 'string' ? a : '') }))
        : [];
      const initialAttachments = attachmentsMapped.length > 0 ? attachmentsMapped : [{ title: '', url: '' }];
      setForm({
        date: createdDate,
        title: isStandardTitle ? data.title : 'other',
        title_custom: isStandardTitle ? '' : (data.title || ''),
        weight: data.weight ?? '',
        temperature: data.temperature ?? '',
        medication: data.medication ?? '',
        notes: data.notes ?? '',
        attachments: initialAttachments
      });
    } catch (err) {
      console.error('Error loading record:', err);
      showToast('Σφάλμα φόρτωσης εγγραφής.', 'error');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAttachmentChange = (index, field, value) => {
    setForm(prev => {
      const next = [...prev.attachments];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, attachments: next };
    });
  };

  const addAttachmentRow = () => {
    setForm(prev => ({ ...prev, attachments: [...prev.attachments, { title: '', url: '' }] }));
  };

  const removeAttachmentRow = (index) => {
    setForm(prev => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== index) }));
  };

  const isValidHttpUrl = (value) => {
    if (!value) return false;
    try {
      const u = new URL(value);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  const updateRecord = async () => {
    if (!form.date || !form.title || (form.title === 'other' && !form.title_custom.trim()) || !form.notes.trim()) {
      showToast('Παρακαλώ συμπληρώστε όλα τα υποχρεωτικά πεδία.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const finalTitle = form.title === 'other' ? form.title_custom.trim() : form.title;
      const attachmentsClean = form.attachments
        .map(a => ({ title: (a.title || '').trim() || null, url: (a.url || '').trim() }))
        .filter(a => a.url.length > 0);

      const dateIso = new Date(form.date).toISOString();

      const { error } = await supabase
        .from('medical_records')
        .update({
          title: finalTitle,
          notes: form.notes.trim(),
          medication: form.medication.trim() || null,
          weight: form.weight ? parseFloat(form.weight) : null,
          temperature: form.temperature ? parseFloat(form.temperature) : null,
          attachments: attachmentsClean,
          created_at: dateIso
        })
        .eq('id', recordId);

      if (error) throw error;

      // Remove warehouse items after successful record update
      if (pendingWarehouseChanges.length > 0) {
        try {
          const result = await removeItems(pendingWarehouseChanges);
          if (result.success) {
            if (import.meta.env.DEV) console.log('Warehouse items removed successfully');
          } else {
            console.warn('Failed to remove warehouse items:', result.message);
          }
        } catch (warehouseError) {
          console.error('Error removing warehouse items:', warehouseError);
          // Don't fail the entire operation if warehouse removal fails
        }
      }

      showToast('Η εγγραφή ενημερώθηκε επιτυχώς!', 'success');
      setTimeout(() => {
        if (pet?.id) {
          navigate(`/pet-medical-history/${pet.id}`);
        } else {
          navigate('/clients');
        }
      }, 1200);
    } catch (err) {
      console.error('Error updating record:', err);
      showToast('Σφάλμα ενημέρωσης εγγραφής.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen text-white flex flex-col lg:flex-row" style={{ backgroundColor: '#1A202C', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, background: 'radial-gradient(800px 800px at 10% -10%, rgba(16,185,129,0.12), transparent), radial-gradient(800px 800px at 110% 10%, rgba(59,130,246,0.08), transparent)' }} className="pointer-events-none" />
      {/* Shared Sidebar */}
      <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />

      {/* Main Content */}
      <div className="flex-1 p-4 lg:p-8">
        {/* Toast Notification */}
        {toast.show && (
          <div className="fixed top-4 right-4 left-4 sm:left-auto z-50">
            <div
              className="flex items-center px-5 py-4 rounded-xl border max-w-sm ml-auto transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(31,41,55,0.95) 100%)',
                backdropFilter: 'blur(10px)',
                borderColor: toast.type === 'success' ? '#10B981' : toast.type === 'error' ? '#EF4444' : '#3B82F6',
                boxShadow: `0 10px 25px rgba(0,0,0,0.3), 0 0 20px ${
                  toast.type === 'success' ? 'rgba(16,185,129,0.2)' :
                  toast.type === 'error' ? 'rgba(239,68,68,0.2)' : 'rgba(59,130,246,0.2)'
                }`
              }}
            >
              <div className="flex-shrink-0 mr-3">
                {toast.type === 'success' && (
                  <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                )}
                {toast.type === 'error' && (
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                )}
              </div>
              <div className="text-white font-medium flex-1">{toast.message}</div>
              <button
                onClick={() => setToast({ show: false, message: '', type: 'success' })}
                className="ml-4 text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Επεξεργασία Ιατρικής Εγγραφής</h1>
              <p className="text-gray-400 text-base lg:text-lg">
                {pet ? `${pet.name} (${pet.species})` : 'Φόρτωση κατοικιδίου...'}
              </p>
            </div>
            <button
              onClick={() => pet?.id ? navigate(`/pet-medical-history/${pet.id}`) : navigate('/clients')}
              className="px-5 py-2.5 rounded-lg font-medium text-gray-300 transition-all duration-200 hover:bg-gray-600"
              style={{ backgroundColor: '#4A5568' }}
            >
              ← Πίσω
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="max-w-4xl">
          <div className="rounded-xl overflow-hidden border transition-all duration-300 hover:border-emerald-500/30" style={{ background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)', borderColor: '#374151', boxShadow: '0 10px 25px rgba(0,0,0,0.3)' }}>
            <div className="px-4 lg:px-6 py-4 border-b" style={{ borderColor: '#374151', background: 'linear-gradient(90deg, rgba(55,65,81,0.8), rgba(31,41,55,0.8))' }}>
              <h3 className="text-lg lg:text-xl font-semibold text:white">Στοιχεία Ιατρικής Εγγραφής</h3>
            </div>

            <div className="p-4 lg:p-6">
              <form onSubmit={(e) => { e.preventDefault(); updateRecord(); }} className="space-y-6">
                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Ημερομηνία Επισκέψεως *</label>
                  <input
                    type="date"
                    name="date"
                    value={form.date}
                    onChange={handleChange}
                    required
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                    style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                  />
                </div>

                {/* Title and custom title */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Τύπος Επισκέψεως *</label>
                    <select
                      name="title"
                      value={form.title}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                      style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                    >
                      <option value="">Επιλέξτε τύπο</option>
                      <option value="check up">Γενικός έλεγχος</option>
                      <option value="vaccination">Εμβολιασμός</option>
                      <option value="surgery">Χειρουργείο</option>
                      <option value="emergency">Επείγον</option>
                      <option value="other">Άλλο</option>
                    </select>
                  </div>

                  {form.title === 'other' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Προσαρμοσμένος Τύπος *</label>
                      <input
                        type="text"
                        name="title_custom"
                        value={form.title_custom}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                        style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                        placeholder="Εισάγετε τον τύπο"
                      />
                    </div>
                  )}
                </div>

                {/* Weight and temperature */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Βάρος (kg)</label>
                    <input
                      type="number"
                      name="weight"
                      value={form.weight}
                      onChange={handleChange}
                      step="0.1"
                      min="0"
                      className="w-full px-3 py-2 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                      style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Θερμοκρασία (°C)</label>
                    <input
                      type="number"
                      name="temperature"
                      value={form.temperature}
                      onChange={handleChange}
                      step="0.1"
                      min="35"
                      max="42"
                      className="w-full px-3 py-2 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                      style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                      placeholder="36.5"
                    />
                  </div>
                </div>

                {/* Medication */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-300">Φάρμακα</label>
                    {warehouseItems && warehouseItems.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowWarehouseDialog(true)}
                        className="flex items-center px-2 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-md transition-colors duration-200 group"
                        title="Προσθήκη προϊόντων από την αποθήκη"
                      >
                        <span className="group-hover:hidden">+</span>
                        <span className="hidden group-hover:inline">Προσθήκη από αποθήκη</span>
                      </button>
                    )}
                  </div>
                  <textarea
                    name="medication"
                    value={form.medication}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-3 py-2 rounded-lg border text-white resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                    style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                    placeholder="Φάρμακα που χορηγήθηκαν..."
                  />
                </div>

                {/* Attachments */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">Συννημμένα</label>
                  <div className="space-y-3">
                    {form.attachments.map((att, idx) => (
                      <div key={idx} className="grid grid-cols-1 lg:grid-cols-[1fr_2fr_auto] gap-3 items-center">
                        <input
                          type="text"
                          value={att.title}
                          onChange={(e) => handleAttachmentChange(idx, 'title', e.target.value)}
                          placeholder="Τίτλος αρχείου (προαιρετικό)"
                          className="w-full px-3 py-2 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                          style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                        />
                        <div>
                          <input
                            type="url"
                            value={att.url}
                            onChange={(e) => handleAttachmentChange(idx, 'url', e.target.value)}
                            placeholder="https://cloud.example.com/file.pdf ή .jpg/.png"
                            className="w-full px-3 py-2 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                            style={{ backgroundColor: '#1F2937', borderColor: att.url && !isValidHttpUrl(att.url) ? '#EF4444' : '#374151' }}
                          />
                          {att.url && !isValidHttpUrl(att.url) && (
                            <p className="mt-1 text-xs" style={{ color: '#FCA5A5' }}>Παρακαλώ εισάγετε έγκυρο URL που ξεκινά με http/https.</p>
                          )}
                        </div>
                        <div className="flex gap-2 justify-end items-center">
                          <a
                            href={isValidHttpUrl(att.url) ? att.url : undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`px-3 py-2 rounded-lg text-white text-xs transition-transform duration-200 ${isValidHttpUrl(att.url) ? 'hover:scale-[1.02]' : 'opacity-50 pointer-events-none'}`}
                            style={{ backgroundColor: '#3B82F6' }}
                          >
                            Προεπισκόπηση
                          </a>
                          {form.attachments.length > 1 && (
                            <button type="button" onClick={() => removeAttachmentRow(idx)} className="px-3 py-2 rounded-lg text-white transition-all duration-200 hover:opacity-90" style={{ backgroundColor: '#EF4444' }}>✕</button>
                          )}
                          {idx === form.attachments.length - 1 && (
                            <button type="button" onClick={addAttachmentRow} className="px-3 py-2 rounded-lg text-white transition-transform duration-200 hover:scale-[1.02]" style={{ background: 'linear-gradient(90deg, #10B981, #059669)' }}>＋</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-gray-400">Τα URLs θα εμφανιστούν στο ιστορικό.</p>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Σημειώσεις *</label>
                  <textarea
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    required
                    rows="4"
                    className="w-full px-3 py-2 rounded-lg border text-white resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                    style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                    placeholder="Σημειώσεις από τον κτηνίατρο, διάγνωση, θεραπεία..."
                  />
                </div>

                {/* Actions */}
                <div className="flex flex-col lg:flex-row justify-end space-y-3 lg:space-y-0 lg:space-x-3 pt-6">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="w-full lg:w-auto px-6 py-3 rounded-lg font-medium text-gray-300 transition-all duration-200 hover:bg-gray-600"
                    style={{ backgroundColor: '#4A5568' }}
                  >
                    Ακύρωση
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full lg:w-auto px-6 py-3 rounded-lg font-medium text-white transition-transform duration-200 hover:scale-[1.02] disabled:opacity-50"
                    style={{ background: 'linear-gradient(90deg, #10B981, #059669)' }}
                  >
                    {submitting ? 'Αποθήκευση...' : 'Αποθήκευση Αλλαγών'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Warehouse Remove Items Dialog */}
      <RemoveItemsDialog 
        isOpen={showWarehouseDialog} 
        onClose={handleWarehouseDialogClose}
        onRemove={handleWarehouseRemove}
      />
    </div>
  );
};

export default EditMedicalRecordPage; 